/**
 * Public entry point for the web admin's telemetry pipe — single import
 * surface for `main.tsx`, `ErrorBoundary`, and the axios interceptor.
 *
 * Concerns split into:
 *   ./sessions    — session lifecycle (start on boot, end on unload)
 *   ./errors      — window.error + unhandledrejection hooks
 *   ./performance — cold-start + screen_load + api_call buffer
 *   ./api         — fetch-based HTTP client for /telemetry/*
 *
 * Everything is best-effort. A misconfigured backend, dropped network,
 * or blocked storage must never break the admin app.
 */

export { startSession, currentSessionToken } from './sessions'
export { installErrorHooks, currentRoute } from './errors'
export {
  recordAppStart, recordScreenLoad, recordApiCall, flushBuffer,
  stopFlushTimer, pathToRoute,
} from './performance'
export { postCrash, postError } from './api'

import { startSession } from './sessions'
import { installErrorHooks } from './errors'
import { recordAppStart, flushBuffer } from './performance'

let initialised = false

/** One-shot boot wiring. Call from main.tsx BEFORE renderApp. Idempotent
 *  so a hot reload during dev doesn't double-install handlers. */
export function initialiseTelemetry(): void {
  if (initialised) return
  initialised = true
  installErrorHooks()
  // Fire-and-forget — session POST and the first app_start can both
  // ride in parallel. No await keeps app render off the critical path.
  void startSession()
  recordAppStart()
  // Final flush attempt during teardown — covers events buffered between
  // the last 10s flush and unload.
  window.addEventListener('beforeunload', () => { void flushBuffer() })
  window.addEventListener('pagehide',     () => { void flushBuffer() })
}
